import { useEffect, useRef, type RefObject } from 'react';
import { toast } from 'react-toastify';
import { navigatePreview } from '~/lib/stores/preview-navigation';
import { TOOL_LABELS, workbenchStore, type ToolType } from '~/lib/stores/workbench';
import { panelNavigateTarget, parsePanelMessage } from './marketplace-model';

const TOOL_IDS = Object.keys(TOOL_LABELS);

/**
 * Sandbox for plugin frames (panels and admin extensions). `allow-same-origin`
 * is for the plugin's own cookies, safe because the frame is never our origin
 * (panelFrameSrc refuses that). `allow-popups-to-escape-sandbox` lets OAuth and
 * Stripe flows opened from the frame run as normal pages.
 */
export const PLUGIN_FRAME_SANDBOX =
  'allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox allow-same-origin';

/**
 * The workbench side of @dunea/plugin-sdk/panel's postMessage protocol for one
 * iframe: toast, navigate (a tool id, or an app path opened in the preview),
 * resize, close. Only messages whose `event.source` is THIS frame are acted on.
 */
export function usePluginFrameMessages(
  frame: RefObject<HTMLIFrameElement | null>,
  opts: { name: string; onResize: (height: number) => void; onClose?: () => void },
): void {
  const optsRef = useRef(opts);
  optsRef.current = opts;

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (!frame.current || event.source !== frame.current.contentWindow) {
        return;
      }

      const message = parsePanelMessage(event.data);

      if (!message) {
        return;
      }

      const { name, onResize, onClose } = optsRef.current;

      switch (message.type) {
        case 'toast': {
          const text = `${name}: ${message.message}`;

          if (message.variant === 'success') {
            toast.success(text);
          } else if (message.variant === 'error') {
            toast.error(text);
          } else {
            toast.info(text);
          }

          break;
        }
        case 'navigate': {
          const target = panelNavigateTarget(message.to, TOOL_IDS);

          if (target?.kind === 'tool') {
            workbenchStore.openTool(target.tool as ToolType);
          } else if (target?.kind === 'path') {
            if (navigatePreview(target.path)) {
              workbenchStore.setActiveTab('preview');
            }
          }

          break;
        }
        case 'resize':
          onResize(message.height);
          break;
        case 'close':
          onClose?.();
          break;
      }
    };

    window.addEventListener('message', onMessage);

    return () => window.removeEventListener('message', onMessage);
  }, [frame]);
}
